import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

const KEY = 'wildswim.prefs.v1';

/**
 * Local-only display preferences (units, time format, map filters).
 * Backend prefs (cold tolerance, CSO patience, swims-alone) live in
 * /v1/me/preferences and are owned by SettingsPage / Onboarding.
 */
export interface LocalPrefs {
    units: 'metric' | 'imperial';
    timeFormat: '12h' | '24h';
    showTideStations: boolean;
    notificationsAsked: boolean;
}

const DEFAULTS: LocalPrefs = {
    units: 'metric',
    timeFormat: '24h',
    showTideStations: false,
    notificationsAsked: false,
};

interface Ctx {
    prefs: LocalPrefs;
    update: (patch: Partial<LocalPrefs>) => void;
}

const PrefsContext = createContext<Ctx>({ prefs: DEFAULTS, update: () => undefined });

function load(): LocalPrefs {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return DEFAULTS;
        const parsed = JSON.parse(raw) as Partial<LocalPrefs>;
        return { ...DEFAULTS, ...parsed };
    } catch {
        return DEFAULTS;
    }
}

export function PrefsProvider({ children }: { children: ReactNode }) {
    const [prefs, setPrefs] = useState<LocalPrefs>(() => load());

    useEffect(() => {
        try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch { /* quota */ }
    }, [prefs]);

    const update = useCallback((patch: Partial<LocalPrefs>) => {
        setPrefs((p) => ({ ...p, ...patch }));
    }, []);

    return <PrefsContext.Provider value={{ prefs, update }}>{children}</PrefsContext.Provider>;
}

export function usePrefs() {
    return useContext(PrefsContext);
}
